import React, { useEffect, useMemo, useState } from 'react';

import { graphql, useStaticQuery } from 'gatsby';

import yml from 'js-yaml';

import ArduinoBoards from '../components/ArduinoBoards/ArduinoBoards';
import Banner from '../components/Banner/Banner';
import CardSection from '../components/CardSection/CardSection';
import ConnectAndContribute from '../components/ConnectAndContribute/ConnectAndContribute';
import GettingStarted from '../components/GettingStarted/GettingStarted';
import GettingStartedModal from '../components/GettingsStartedModal/GettingStartedModal';
import { formatGettingStartedData } from '../components/GettingsStartedModal/useGettingStartedMap';
import Layout from '../components/Layout';
import PageTitle from '../components/PageTitle/PageTitle';
import SideBar from '../components/SideBar/SideBar';
import SEO from '../components/seo';
import { MAKER, PRO, hardwareCard } from '../utils/constants';

//import ArduinoBoardsMobile from '../components/ArduinoBoards/ArduinoBoardsMobile';
//import HardwareSkeleton from '../components/Skeletons/HardwareSkeleton';
import { getHardwareSideBarDataRefactored } from '../utils/functions';
import './hardware.scss';

const gettingStartedsSw = ['ide-v1', 'ide-v2', 'web-editor'];

interface HardwarePageProps {
  featured: any;
  featuredImage: any;
  families: {
    edges: Array<any>;
  };
  categories: {
    edges: Array<any>;
  };
  images: {
    edges: Array<any>;
  };
  images480: {
    edges: Array<any>;
  };
  products: {
    edges: Array<any>;
  };
  gettingstarted: {
    edges: Array<any>;
  };
  suggestions: {
    edges: Array<any>;
  };
  compatibilities: {
    edges: Array<any>;
  };
}

export default function Hardware(): React.ReactElement {
  const data: HardwarePageProps = useStaticQuery(pageQuery);

  const [openGettingStartedModal, setOpenGettingStartedModal] = useState(false);
  const [selectedBoard, setSelectedBoard] = useState<any>(null);
  const [selectedBoardCompatibility, setSelectedBoardCompatibility] = useState<any>(null);
  const [chosenSoftware, setChosenSoftware] = useState<string | null>(null);
  //const isDesktop = typeof window !== 'undefined' ? window.innerWidth > 768 : true;
  // const [isFetching, setIsFetching] = useState(false);

  // useEffect(() => {
  //   if (isFetching) {
  //     setTimeout(() => {
  //       setIsFetching(false);
  //     }, 500);
  //   }
  // }, [isFetching]);

  const gettingStartedProducts = useMemo(() => {
    return data.products.edges.filter((hw) => hw.node.fields && hw.node.fields.category === 'board');
  }, [data.products]);

  const selectOptions = useMemo(
    () =>
      gettingStartedProducts.map((p) => {
        return { value: p.node.fields.slug, label: p.node.childMdx.frontmatter.title };
      }),
    [gettingStartedProducts]
  );

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const board = urlParams.get('board');
    const sw = urlParams.get('sw');
    const selectedBoard = gettingStartedProducts.find((p) => p.node.fields.slug === `/hardware/${board}`);
    if (selectedBoard) {
      setSelectedBoardCompatibility(data.compatibilities.edges.find((c) => c.node.relativeDirectory === selectedBoard.node.relativeDirectory));
      setSelectedBoard(selectedBoard);
      if (gettingStartedsSw.find((s) => s === sw)) {
        setChosenSoftware(sw);
      }
      setOpenGettingStartedModal(true);
    }
  }, [data.compatibilities.edges, gettingStartedProducts]);

  const hardwareRefactoredData = useMemo(() => {
    return getHardwareSideBarDataRefactored(data);
  }, [data]);
  return (
    <Layout>
      <SEO title="Hardware" description="Browse through hundreds of tutorials, datasheets and guides for all Arduino products." />
      <main className="page-container">
        <SideBar links={[]} nestedCategories={hardwareRefactoredData} actualPage={'Hardware'} />
        <div className="page-layout hardware">
          <PageTitle title="Hardware" description="Browse through hundreds of tutorials, datasheets and guides for all Arduino products." />
          {/* {isFetching ? (
            <>
              <HardwareSkeleton />
              <br />
              <HardwareSkeleton />
            </>
          ) : ( */}
          <div className="hardware__boards">
            <ArduinoBoards boardData={hardwareRefactoredData} type={MAKER} />
            <ArduinoBoards boardData={hardwareRefactoredData} type={PRO} />
            {/* <ArduinoBoards boardData={hardwareRefactoredData} type="edu" /> */}
          </div>
          {/* )} */}
          <Banner
            title={data.featured.childMdx.frontmatter.title}
            label="FEATURED"
            description={data.featured.childMdx.frontmatter.description}
            image={data.featuredImage}
            cta={[{ url: data.featured.childMdx.frontmatter.url, label: 'DISCOVER MORE' }]}
            svg={true}
            bottom={true}
          />
          <GettingStarted
            setSelectedBoardCompatibility={(value) => setSelectedBoardCompatibility(value)}
            setSelectedBoard={(value) => setSelectedBoard(value)}
            setOpenGettingStartedModal={(value) => setOpenGettingStartedModal(value)}
            selectOptions={selectOptions}
            selectedBoard={selectedBoard}
            boardData={data}
            gettingStartedProducts={gettingStartedProducts}
          />
          <CardSection title={null} cardList={hardwareCard} />
          <ConnectAndContribute />
        </div>
      </main>

      {openGettingStartedModal && (
        <GettingStartedModal
          data={formatGettingStartedData(data)}
          isOpen={openGettingStartedModal}
          core={selectedBoard.node.childMdx.frontmatter.core}
          product={{
            name: selectedBoard.node.childMdx.frontmatter.title,
            slug: selectedBoard.node.fields.slug,
            compatibility: yml.load(selectedBoardCompatibility.node.fields.content),
          }}
          onClose={() => setOpenGettingStartedModal(false)}
          chosenSoftware={chosenSoftware}
          suggestions={data.suggestions.edges.find((b) => b.node.relativeDirectory === selectedBoard.node.relativeDirectory)?.node}
        />
      )}
    </Layout>
  );
}

export const pageQuery = graphql`
  {
    featured: file(relativePath: { eq: "hardware/featured.md" }) {
      relativeDirectory
      childMdx {
        body
        frontmatter {
          title
          description
          url
        }
      }
    }

    featuredImage: file(
      relativeDirectory: { eq: "hardware" }
      base: {regex: "/\\image.(jpe?g|png|gif|bmp|svg)$/i"}
    ) {
      childImageSharp {
        gatsbyImageData(layout: FIXED, width: 354, quality: 90, placeholder: BLURRED)
      }
      extension
      publicURL
    }
    families: allFile(filter: { base: { eq: "family.md" } }, sort: { fields: { order: ASC } }) {
      edges {
        node {
          relativeDirectory
          childMdx {
            frontmatter {
              title
              description
              businessUnit
            }
          }
        }
      }
    }
    categories: allFile(filter: { base: { eq: "category.md" } }) {
      edges {
        node {
          relativeDirectory
          childMdx {
            frontmatter {
              title
            }
          }
        }
      }
    }
    products: allFile(filter: { base: { eq: "product.md" } }) {
      edges {
        node {
          relativeDirectory
          fields {
            slug
            category
          }
          childMdx {
            frontmatter {
              title
              core
            }
          }
        }
      }
    }
    compatibilities: allFile(filter: { base: { eq: "compatibility.yml" } }) {
      edges {
        node {
          relativeDirectory
          fields {
            content
          }
        }
      }
    }
    suggestions: allFile(filter: { base: { eq: "suggestions.md" } }) {
      edges {
        node {
          relativeDirectory
          childMdx {
            body
          }
        }
      }
    }
    gettingstarted: allFile(filter: { fields: { type: { eq: "softwarepage" }, gettingstarted: { ne: null } } }, sort: { fields: { slug: ASC } }) {
      edges {
        node {
          fields {
            slug
            gettingstarted
            software
            core
          }
          childMdx {
            body
            tableOfContents
            frontmatter {
              title
              description
            }
          }
        }
      }
    }
    images480: allFile(filter: { base: { eq: "image-480.png" } }) {
      edges {
        node {
          relativeDirectory
          childImageSharp {
            gatsbyImageData(layout: CONSTRAINED, placeholder: BLURRED)
          }
        }
      }
    }
    images: allFile(filter: { base: { eq: "image.svg" } }) {
      edges {
        node {
          relativeDirectory
          childImageSharp {
            gatsbyImageData(layout: CONSTRAINED, placeholder: BLURRED)
          }
        }
      }
    }
  }
`;
